import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import _ from "lodash";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { BackButton, Button, SaveButton } from "../../components/Button";
import { InputSelector } from "../../components/Forms/InputSelector";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import apiAgent from "../../api/apiAgent";
import { tMsg } from "../../components/ToastNotifications";
import { useSetChangesMade } from "../../components/Publishing/utils";
import {
  ConfirmationPopup,
  DeletePopup,
} from "../../components/ConfirmationPopup";
import {
  BaseStringArray,
  StringArrayItem,
  TextInputForStringArray,
} from "../../components/Forms/stringArrayComponents";
import {
  removeQuotes,
  useModifyTagsFromNodes,
  useRemoveTagsFromNodes,
} from "./removeTagsFromNodes";
import { FieldName } from "../../components/Forms/Inputs";
import { ValueItem } from "./ValueItem";
import { EditValuePopup } from "./editValuePopup";

const UpdateOptions = ({
  itemName,
}: {
  itemName: "topics" | "place";
}): JSX.Element => {
  const navigate = useNavigate();
  const removeTagsFromNodes = useRemoveTagsFromNodes(itemName);
  const modifyTagsFromNodes = useModifyTagsFromNodes(itemName);
  // const itemName = params.item?.toString() || "";
  const singularName = itemName === "topics" ? "topic" : "place";

  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [showEditPopup, setShowEditPopup] = useState<boolean>(false);

  const [rawSchema, setRawSchema] = useState<string>("");
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [values, setValues] = useState<string[]>([]);

  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await apiAgent.SingleItem.get(itemName);
      // console.log(response);

      if (response.status === 200) {
        const getData = JSON.parse(response.data.data);
        setRawSchema(response.data.schema);
        setValues(removeQuotes(getData.values || []));
      } else {
        tMsg.error(`${itemName} does not exist`);
        navigate("/error");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoadingData(false);
    }
  };

  const saveItem = async (newData: any) => {
    try {
      // update item api call
      const response = await apiAgent.SingleItem.update(
        itemName,
        newData,
        rawSchema
      );
      if (response.status === 200) {
        // setChangesMade();
        return true;
      } else throw response.statusText;
    } catch (err) {
      tMsg.error(`Error changing ${itemName}`);
      console.log(err);
      return false;
    }
  };

  const handleAddItem = async (value: any) => {
    if (_.includes(values, value)) {
      tMsg.info(`"${value}" already exists.`);
      return;
    }
    const newList = [...values, value];
    setValues(newList);
    const newData = { values: newList };
    const trySave = await saveItem(newData);
    if (trySave)
      tMsg.success(`${_.capitalize(singularName)} successfully added`);
  };

  const handleDeleteSelectedIndex = async () => {
    if (!selectedValue) return;
    const newList = values.filter((v) => v !== selectedValue);
    setValues(newList);
    const newData = { values: newList };
    const trySave = await saveItem(newData);
    if (trySave) {
      tMsg.success(`"${selectedValue}" deleted`);
      removeTagsFromNodes([selectedValue]);
    }
    setShowDeletePopup(false);
    setSelectedValue(null);
  };

  const handleEditValue = async (oldValue: string, newValue: string) => {
    if (newValue.length < 1) {
      tMsg.warning("Cannot modify this value to an empty string.");
    } else if (oldValue !== newValue) {
      const newList = values.map((v) => {
        return v === oldValue ? newValue : v;
      });
      console.log("newList", newList);
      const newData = { values: newList };
      setValues(newList);
      const trySave = await saveItem(newData);
      if (trySave) {
        tMsg.success(`"${selectedValue}" changed to ${newValue}`);
        await modifyTagsFromNodes(oldValue, newValue);
      }
    }
    setShowEditPopup(false);
    setSelectedValue(null);
  };

  const crumbs = [
    {
      label: `Settings`,
      link: `/si/settings`,
    },
  ];

  return (
    <PageContainer isLoading={isLoadingData} className="relative">
      <PageContent reduceHeightForFooter>
        <BreadCrumbs crumbs={crumbs} />
        <TitleBar title={`Edit ${itemName}`}>
          <BackButton />
        </TitleBar>
        <div className="text-sm mb-4">
          <span className="font-bold text-warningRed">IMPORTANT :</span> All
          changes on this page are saved automatically
        </div>
        <div className="flex flex-col gap-3">
          <FieldName name={`Add a new ${singularName}`} />
          <TextInputForStringArray
            addValueFunction={(val: string) => handleAddItem(val)}
          />
          <hr className="my-2" />
          <FieldName name={itemName === "topics" ? "Topics" : "Places"} />
          <div className="grid grid-cols-3 gap-3">
            {isLoadingData ||
              values.map((value, index) => (
                <ValueItem
                  name={value}
                  key={`${index}_${value}`}
                  onDelete={() => {
                    setSelectedValue(value);
                    setShowDeletePopup(true);
                  }}
                  onEdit={() => {
                    setSelectedValue(value);
                    setShowEditPopup(true);
                  }}
                />
              ))}
          </div>
        </div>
      </PageContent>
      <PageFooterBar>
        <Button onClick={() => navigate("/si/settings")}>Done</Button>
      </PageFooterBar>
      <DeletePopup
        showModal={showDeletePopup}
        setShowModal={setShowDeletePopup}
        confirmAction={handleDeleteSelectedIndex}
        CustomMessage={
          <div className="max-w-[16rem] space-y-4">
            <p className="text-center">
              {`Are you sure you want to delete `}
              <span className="font-bold">{selectedValue}</span>?
            </p>
            <p className="text-xs">
              <span className="font-bold text-red-500 text-xs">WARNING :</span>
              {` Deleting this ${singularName}
              will remove it from all associated Moment & Landmark nodes.`}
            </p>
          </div>
        }
      />
      {selectedValue && (
        <EditValuePopup
          showModal={showEditPopup}
          setShowModal={setShowEditPopup}
          editValueAction={handleEditValue}
          cancelAction={() => setSelectedValue(null)}
          value={selectedValue}
          fieldName={singularName}
        />
      )}
    </PageContainer>
  );
};

export default UpdateOptions;
